// Push Notifications Mixin for Vue Components

// VAPID public key from server - should match the environment
const VAPID_PUBLIC_KEY = process.env.VUE_APP_VAPID_PUBLIC_KEY || 'BEqzHkDuq7J8k1x5Gu-Qks7rhz-7k4IPlOoYJYdP9pU-JR8g19lfODq_2GD2T_Msd8bp9m6_3RkqUDdaq8TacGA'

export default {
  data() {
    return {
      pushNotificationSupported: false,
      pushNotificationSubscription: null,
      pushNotificationPermission: 'default'
    }
  },

  computed: {
    pushNotificationsEnabled() {
      return this.pushNotificationSupported && 
             this.pushNotificationPermission === 'granted' && 
             this.pushNotificationSubscription !== null
    }
  },

  mounted() {
    this.checkPushNotificationSupport()
  },

  methods: {
    checkPushNotificationSupport() {
      // Check if browser supports push notifications
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        this.pushNotificationSupported = true
        this.pushNotificationPermission = Notification.permission
        
        // Check if already subscribed
        this.checkExistingSubscription()
      }
    },

    async checkExistingSubscription() {
      try {
        const registration = await navigator.serviceWorker.ready
        const subscription = await registration.pushManager.getSubscription()
        this.pushNotificationSubscription = subscription
        
        // If we have a subscription, verify it's still valid on server
        if (subscription) {
          await this.verifySubscriptionOnServer(subscription)
        }
      } catch (error) {
        console.error('Error checking push subscription:', error)
      }
    },

    async verifySubscriptionOnServer(subscription) {
      try {
        const url = process.env.VUE_APP_API_URI + '/push/verify-subscription'
        const response = await this.axios.post(url, {
          endpoint: subscription.endpoint
        })
        
        const result = response.data
        
        if (!result.valid) {
          console.log('Subscription not valid on server:', result.message)
          
          if (result.subscription_found && result.action === 'resubscribe') {
            // Endpoint mismatch - re-send current subscription
            console.log('Re-sending subscription due to endpoint mismatch')
            await this.sendSubscriptionToServer(subscription)
          } else if (!result.subscription_found) {
            // Server has no subscription - send current one
            console.log('Server has no subscription - sending current subscription')
            await this.sendSubscriptionToServer(subscription)
          }
        } else {
          console.log('Subscription verified on server')
        }
      } catch (error) {
        console.error('Error verifying subscription on server:', error)
        // If verification fails, try to re-send subscription as fallback
        try {
          await this.sendSubscriptionToServer(subscription)
          console.log('Re-sent subscription after verification error')
        } catch (resendError) {
          console.error('Failed to re-send subscription:', resendError)
        }
      }
    },

    async requestPushNotificationPermission() {
      try {
        const permission = await Notification.requestPermission()
        this.pushNotificationPermission = permission
        
        if (permission === 'granted') {
          await this.subscribeToPushNotifications()
        }
        
        return permission
      } catch (error) {
        console.error('Error requesting notification permission:', error)
        throw error
      }
    },

    async subscribeToPushNotifications() {
      try {
        const registration = await navigator.serviceWorker.ready
        
        // Convert base64 string to Uint8Array
        const vapidPublicKey = this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
        
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: vapidPublicKey
        })
        
        this.pushNotificationSubscription = subscription
        
        // Send subscription to server
        await this.sendSubscriptionToServer(subscription)
        
        console.log('Push notification subscription successful')
        return subscription
      } catch (error) {
        console.error('Failed to subscribe to push notifications:', error)
        throw error
      }
    },

    async unsubscribeFromPushNotifications() {
      try {
        if (this.pushNotificationSubscription) {
          await this.pushNotificationSubscription.unsubscribe()
          
          // Notify server about unsubscription
          await this.removeSubscriptionFromServer(this.pushNotificationSubscription)
          
          this.pushNotificationSubscription = null
          console.log('Unsubscribed from push notifications')
        }
      } catch (error) {
        console.error('Error unsubscribing from push notifications:', error)
        throw error
      }
    },

    async sendSubscriptionToServer(subscription) {
      try {
        const url = process.env.VUE_APP_API_URI + '/push/subscribe'
        const response = await this.axios.post(url, {
          subscription: subscription.toJSON(),
          user_id: this.$store.state.user && this.$store.state.user.user_id
        })
        
        console.log('Subscription sent to server:', response.data)
        return response.data
      } catch (error) {
        console.error('Error sending subscription to server:', error)
        throw error
      }
    },

    async removeSubscriptionFromServer(subscription) {
      try {
        const url = process.env.VUE_APP_API_URI + '/push/unsubscribe'
        await this.axios.post(url, {
          endpoint: subscription.endpoint
        })
        
        console.log('Subscription removed from server')
      } catch (error) {
        console.error('Error removing subscription from server:', error)
      }
    },

    // Test notification (for development)
    async sendTestNotification() {
      if (!this.pushNotificationsEnabled) {
        console.error('Push notifications not enabled')
        return
      }

      try {
        const url = process.env.VUE_APP_API_URI + '/push/test'
        const data = {}
        
        // Include current device's endpoint if available
        if (this.pushNotificationSubscription && this.pushNotificationSubscription.endpoint) {
          data.endpoint = this.pushNotificationSubscription.endpoint
        }
        
        const response = await this.axios.post(url, data)
        
        console.log('Test notification sent:', response.data)
      } catch (error) {
        console.error('Error sending test notification:', error)
        throw error
      }
    },

    // Utility function to convert base64 string to Uint8Array
    urlBase64ToUint8Array(base64String) {
      const padding = '='.repeat((4 - base64String.length % 4) % 4)
      const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/')
      
      const rawData = window.atob(base64)
      const outputArray = new Uint8Array(rawData.length)
      
      for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i)
      }
      
      return outputArray
    }
  }
}